import apiClient from './apiClient';
import { connectionsApi } from './connections';
import { unwrapResponse } from './unwrapResponse';
import type {
  ChatConversation,
  ChatMessage,
  ChatMessageAttachmentInput,
  ChatMessagesPagination,
  ChatPresence,
  ChatReaction,
  ConnectionSearchUser,
} from '@/types/api';

type MessagesResponse = {
  data: ChatMessage[];
  pagination: ChatMessagesPagination;
};

export const chatApi = {
  listConversations: async (): Promise<ChatConversation[]> => {
    const res = await apiClient.get('/member/chat/conversations');
    return unwrapResponse<ChatConversation[]>(res);
  },

  createConversation: async (friendId: string): Promise<ChatConversation> => {
    const res = await apiClient.post('/member/chat/conversations', { friendId });
    return unwrapResponse<ChatConversation>(res);
  },

  getMessages: async (
    conversationId: string,
    page = 1,
    limit = 30
  ): Promise<{ data: ChatMessage[]; pagination: ChatMessagesPagination }> => {
    const res = await apiClient.get(
      `/member/chat/conversations/${conversationId}/messages?page=${page}&limit=${limit}`
    );
    const unwrapped = unwrapResponse<MessagesResponse | ChatMessage[]>(res);
    if (Array.isArray(unwrapped)) {
      return {
        data: unwrapped,
        pagination: {
          page,
          limit,
          perPage: limit,
          nextPage: null,
          totalPages: 1,
          total: unwrapped.length,
        },
      };
    }
    return unwrapped;
  },

  sendMessage: async (
    conversationId: string,
    body: string,
    replyToMessageId?: string | null,
    attachments?: ChatMessageAttachmentInput[]
  ): Promise<ChatMessage> => {
    const res = await apiClient.post(`/member/chat/conversations/${conversationId}/messages`, {
      body,
      ...(replyToMessageId ? { replyToMessageId } : {}),
      ...(attachments && attachments.length ? { attachments } : {}),
    });
    return unwrapResponse<ChatMessage>(res);
  },

  editMessage: async (
    conversationId: string,
    messageId: string,
    body: string
  ): Promise<ChatMessage> => {
    const res = await apiClient.put(
      `/member/chat/conversations/${conversationId}/messages/${messageId}`,
      { body }
    );
    return unwrapResponse<ChatMessage>(res);
  },

  deleteMessage: async (conversationId: string, messageId: string): Promise<ChatMessage> => {
    const res = await apiClient.delete(
      `/member/chat/conversations/${conversationId}/messages/${messageId}`
    );
    return unwrapResponse<ChatMessage>(res);
  },

  addReaction: async (
    conversationId: string,
    messageId: string,
    emoji: string
  ): Promise<ChatReaction> => {
    const res = await apiClient.post(
      `/member/chat/conversations/${conversationId}/messages/${messageId}/reactions`,
      { emoji }
    );
    return unwrapResponse<ChatReaction>(res);
  },

  removeReaction: async (conversationId: string, messageId: string): Promise<void> => {
    await apiClient.delete(
      `/member/chat/conversations/${conversationId}/messages/${messageId}/reactions`
    );
  },

  markDelivered: async (
    conversationId: string
  ): Promise<{ messageIds: string[] }> => {
    const res = await apiClient.post(`/member/chat/conversations/${conversationId}/delivered`);
    return unwrapResponse<{ messageIds: string[] }>(res);
  },

  markRead: async (conversationId: string): Promise<{ messageIds: string[] }> => {
    const res = await apiClient.post(`/member/chat/conversations/${conversationId}/read`);
    return unwrapResponse<{ messageIds: string[] }>(res);
  },

  setTyping: async (conversationId: string, isTyping: boolean): Promise<void> => {
    await apiClient.post(`/member/chat/conversations/${conversationId}/typing`, { isTyping });
  },

  getPresence: async (userIds: string[]): Promise<ChatPresence[]> => {
    if (!userIds.length) return [];
    const q = userIds.map((id) => `userIds[]=${encodeURIComponent(id)}`).join('&');
    const res = await apiClient.get(`/member/chat/presence?${q}`);
    return unwrapResponse<ChatPresence[]>(res);
  },

  updatePresence: async (isOnline: boolean, options?: { keepalive?: boolean }): Promise<void> => {
    await apiClient.post(
      '/member/chat/presence',
      { isOnline },
      options?.keepalive ? { keepalive: true } : undefined
    );
  },

  discoverPeople: async (params: {
    page?: number;
    limit?: number;
    name?: string;
    gender?: string;
    minAge?: number;
    maxAge?: number;
  }): Promise<{ data: ConnectionSearchUser[]; total: number }> => {
    return connectionsApi.searchUsers({
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      name: params.name,
      gender: params.gender,
      minAge: params.minAge,
      maxAge: params.maxAge,
    });
  },
};

export default chatApi;
